// @givmo/passkeys — the "Sign-in & security" card (A4; AS-WEB11; D-AS-21's approved placement).
//
// One account-wide card, written once and placed by each console: first under Settings › Your
// settings on the charity console, and as its own page on the employer portal (which titles it, so
// it renders `headless` there). It covers the person's GIVMO ACCOUNT everywhere they sign in, never
// the organization's.
//
// THE PASSKEYS ROW, the account reset (AS-WEB13), and the frame's "Authenticator app" and "Recovery
// codes" rows (AS-WEB14, `AuthenticatorRows`): drawn while the backend serves authenticator apps,
// their actions only for an account the web offers authenticators to (Google, Apple or Microsoft
// sign-ins, D-AS-26 N2) on a console that hands over its enrolment primitives (`authenticator`).
//
// The reset (AS-WEB13): a pending one leads the card as its notice, with "Wasn't me — cancel it" —
// the web's place to cancel, which every email and screen names (`PendingFactorResetNotice`); and
// "Can't use a passkey?" under the passkeys opens the reset itself, the host's `renderLost` drawn in
// the card's own dialog (the same view a lost link opens inside the "Confirm it's you" sheet). It is
// offered while the account holds a passkey and the host supplies the reset, also while one is
// pending: coming back after the wait to finish is the same door. Once the wait has run, the notice
// offers "Finish the reset" too (AS-WEB17): the host's view opened on the emailed reset's finish
// (`wayOut`), the one door to it for an account with no passkey.
//
// The passkeys row (frame A4, words as approved; D-AS-35 decision 3, fix 7 and #14):
//   - its line says "…instead of a password." only to an account that can sign in with one (its
//     host's ways in list a password); every other account reads "Sign in with your fingerprint or
//     face.", since a passkey replaces no password there;
//   - "Set up a passkey" with none, "Add another passkey" with one or more (when this browser can
//     create one), running `useAddPasskey` — the factor rules' refusals open "Confirm it's you"; the
//     server's passkey limit says so ("You've added as many passkeys as we can store…");
//   - each passkey: its name (or "Passkey", iOS's word for an unnamed one), then "Backed up" or
//     "This device only", "Added {date}" and "Last used today | {date}", and a menu with "Rename" and
//     "Remove";
//   - "Rename" opens a small dialog ("Rename", "Name", "Save", "Cancel"; an empty name clears it);
//   - "Remove" asks first (iOS's shipped "Remove this passkey?"), then removes through the gate: one
//     of several needs the 30-minute step-up only while `STEP_UP_AUTH_ENABLED` is on, the LAST one
//     always needs that passkey's own confirmation (backend rule 2, the passkey-only sheet).
//
// ⚠ IT RENDERS NOTHING UNTIL `GET /users/me/passkeys` ANSWERS 200. Dark (404), refused or failed, the
// card is absent: the backend's flag is the only switch, as for every passkey surface. Once served, a
// failed re-read keeps it and only the dark 404 withdraws it (`servedData`); an add or a removal that
// did not complete re-reads the account's factors, so the next attempt runs on the server's answer.
//
// UNDER A SIGN-IN LOCK (AS-WEB23; frame E1, DRAFT words; `sign-in-lock.ts`): AS-BE81's
// `sign_in_lock.active` on the security read, the card's one reading, passed down to the rows. One
// line says why, under a pending reset's alert, which stays (every reset email names its cancel, and
// the apps keep it under a lock too). The passkeys, the authenticator app and its recovery codes stay
// listed as plain facts, since the lock pauses them and deletes nothing. Every button goes, and with
// them the pitch line and "Can't use a passkey?": each would end in a confirmation, or a passkey, the
// lock refuses. A factor the account doesn't hold has nothing left to say, so its row goes too. A page
// drawn before the lock bound re-reads before any setup starts (`useAddPasskey`).
//
// ⚠ THE PASSKEYS ROW OFFERS NOTHING UNTIL THE LOCK IS KNOWN (`useSignInLockAnswered`). The list and the
// security read answer separately, and while the security read is on the wire a lock-bound account
// read as unlocked, so the row drew its add, its menus and "Can’t use a passkey?" and then withdrew
// them (cut 1009b's review, F3). Until the read answers, the row is its facts alone (the authenticator
// rows already wait for that read). A pending reset's "Wasn’t me — cancel it" keeps the lock, too: the
// cancel's narrow answer keeps the full read's statement (`recordFactorResetCancelled`).

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  ErrorMessage,
  RowActions,
  TextField,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { isPasskeyRegistrationSupported } from '@givmo/auth';
import type { PasskeyOut } from '@givmo/api-client';
import {
  isStepUpCancelled,
  useOptionalStepUpHost,
  useStepUpGate,
  type StepUpLostFactor,
  type StepUpLostWayOut,
} from '@givmo/step-up';
import {
  MY_PASSKEYS_QUERY_KEY,
  invalidateAccountFactors,
  servedData,
  useMyPasskeys,
} from './account-factors';
import { AuthenticatorRows } from './authenticator-rows';
import type { AuthenticatorControls } from './authenticator-setup';
import { passkeyDetail, passkeyLabel } from './passkey-dates';
import { PendingFactorResetNotice } from './pending-factor-reset-notice';
import { removePasskey, renamePasskey } from './passkey-registration-transport';
import { passkeyRefusalCodes, passkeyStepUpCopy } from './passkey-step-up-copy';
import {
  signInLockCardLine,
  useSignInLock,
  useSignInLockAnswered,
  type SignInLock,
} from './sign-in-lock';
import { PASSKEY_LIMIT_LINE, SIGN_IN_AGAIN_LABEL, useAddPasskey } from './use-add-passkey';

export const SIGN_IN_SECURITY_ID = 'sign-in-security';

/** The card's one failure line, borrowed from the sheet's approved words (B3d). */
const FAILED_LINE = 'That didn’t work. Try again.';

const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

export interface SignInSecurityCardProps {
  /** The signed-in account's uid: every read is keyed by it. */
  uid: string | null;
  /** Leave out the card's own heading, for a page that already carries it. */
  headless?: boolean;
  /**
   * The console's authenticator-app primitives (AS-WEB14). Absent, the authenticator rows show the
   * account's state with no action.
   */
  authenticator?: AuthenticatorControls;
}

export function SignInSecurityCard({
  uid,
  headless = false,
  authenticator,
}: SignInSecurityCardProps) {
  const passkeys = servedData(useMyPasskeys(uid));
  if (passkeys === undefined) return null;
  return (
    <SignInSecurityCardBody
      uid={uid}
      passkeys={passkeys}
      headless={headless}
      authenticator={authenticator}
    />
  );
}

function SignInSecurityCardBody({
  uid,
  passkeys,
  headless,
  authenticator,
}: {
  uid: string | null;
  passkeys: readonly PasskeyOut[];
  headless: boolean;
  authenticator: AuthenticatorControls | undefined;
}) {
  const queryClient = useQueryClient();
  const host = useOptionalStepUpHost();
  const holdsAuthenticator = host?.holdsAuthenticator;
  const renderLost = host?.renderLost;
  // AS-WEB23: the one reading of the lock, for this card and its rows alike.
  const lock = useSignInLock(uid);
  const locked = lock.active;
  // The passkeys row's actions, only once the lock is known and binds nothing (F3).
  const offers = useSignInLockAnswered(uid) && !locked;
  /** The reset the card's own dialog shows: "Can't use a passkey?", or the notice's finish. */
  const [resetting, setResetting] = useState<{
    factor: StepUpLostFactor;
    wayOut?: StepUpLostWayOut;
  } | null>(null);
  const {
    add,
    pending: adding,
    limitReached,
    signInNeeded,
    dialog: addDialog,
  } = useAddPasskey('passkey-card-step-up', { uid });
  // AS-PWL-2e: a first passkey only a new sign-in can add now — the host's way there, if any.
  const signInAgain = signInNeeded !== null ? host?.signInAgain : undefined;
  // #14: "instead of a password" only for an account that can sign in with one.
  const holdsPassword = host?.waysIn.some((way) => way.kind === 'password') === true;
  const [failed, setFailed] = useState(false);
  const [renaming, setRenaming] = useState<PasskeyOut | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState<PasskeyOut | null>(null);
  const [removing, setRemoving] = useState<PasskeyOut | null>(null);

  // The removal's own gate: its words name the passkey being removed (B1).
  const removalCopy = useMemo(
    () => passkeyStepUpCopy(removing === null ? null : { name: removing.name ?? null }),
    [removing],
  );
  const refusalCodes = useMemo(() => passkeyRefusalCodes(holdsAuthenticator), [holdsAuthenticator]);
  const removalGate = useStepUpGate({
    copy: removalCopy,
    testIdPrefix: 'passkey-card-step-up',
    refusalCodes,
  });

  const now = new Date();
  const canAdd = isPasskeyRegistrationSupported();

  const onAdd = async () => {
    setFailed(false);
    const outcome = await add();
    if (outcome === 'failed') setFailed(true);
  };

  const onRemove = async (passkey: PasskeyOut) => {
    setConfirmingRemoval(null);
    setFailed(false);
    setRemoving(passkey);
    try {
      await removalGate.run(() => removePasskey(passkey.id));
      await queryClient.invalidateQueries({ queryKey: MY_PASSKEYS_QUERY_KEY });
    } catch (thrown) {
      if (!isStepUpCancelled(thrown)) {
        setFailed(true);
        void invalidateAccountFactors(queryClient);
      }
    } finally {
      setRemoving(null);
    }
  };

  return (
    // The account menu's row lands here (`#sign-in-security`): the margin keeps the heading clear of
    // a console's sticky bar, as org-console's anchored cards do (EP-MNT18).
    <Card id={SIGN_IN_SECURITY_ID} className="scroll-mt-[120px]" testId="sign-in-security">
      {!headless && (
        <CardHeader
          title="Sign-in & security"
          subtitle="For your Givmo account, everywhere you sign in."
        />
      )}
      <CardBody flush className="flex flex-col divide-y divide-border-subtle">
        <PendingFactorResetNotice
          uid={uid}
          {...(renderLost !== undefined
            ? {
                onFinish: (factor: StepUpLostFactor) =>
                  setResetting({ factor, wayOut: 'emailReset' }),
              }
            : {})}
        />
        {locked && <SignInLockLine lock={lock} />}
        {/* Under a lock, or before the lock is known, the row is its facts alone, and an account with no
            passkey has none. */}
        {(offers || passkeys.length > 0) && (
          <div
            className="flex flex-col gap-14 px-20 py-16"
            {...testIdProp('sign-in-security-passkeys')}
          >
            <div className="flex flex-wrap items-start justify-between gap-x-16 gap-y-10">
              <div className="flex min-w-0 flex-col gap-2">
                <h4 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  Passkeys
                </h4>
                {offers && (
                  <p className={QUIET} {...testIdProp('sign-in-security-passkeys-line')}>
                    {holdsPassword
                      ? 'Sign in with your fingerprint or face instead of a password.'
                      : 'Sign in with your fingerprint or face.'}
                  </p>
                )}
              </div>
              {!offers ? null : signInAgain !== undefined ? (
                // AS-PWL-2e: only a new sign-in adds the passkey now, so the row's one action becomes
                // the host's way there, in the add button's own slot and style (the offer does too).
                <div className="shrink-0">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    onClick={signInAgain}
                    testId="sign-in-security-sign-in-again"
                  >
                    {SIGN_IN_AGAIN_LABEL}
                  </Button>
                </div>
              ) : (
                canAdd && (
                  <div className="shrink-0">
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      loading={adding}
                      onClick={() => void onAdd()}
                      testId="sign-in-security-add-passkey"
                    >
                      {passkeys.length > 0 ? 'Add another passkey' : 'Set up a passkey'}
                    </Button>
                  </div>
                )
              )}
            </div>
            {/* A failure belongs to an action, and a lock leaves none to retry. */}
            {failed && offers && (
              <ErrorMessage className="pt-0" testId="sign-in-security-failed">
                {limitReached ? PASSKEY_LIMIT_LINE : FAILED_LINE}
              </ErrorMessage>
            )}
            {signInNeeded !== null && offers && (
              // AS-PWL-2e: the refusal's own words, never "Try again", which a retry can't make true.
              <ErrorMessage className="pt-0" testId="sign-in-security-sign-in-needed">
                {signInNeeded}
              </ErrorMessage>
            )}
            {passkeys.length > 0 && (
              // The passkeys depend on the row above: a leading rule, never a fill or an indent alone.
              <ul className="flex flex-col gap-12 border-l-2 border-border pl-16">
                {passkeys.map((passkey) => (
                  <li
                    key={passkey.id}
                    className="flex items-center justify-between gap-12"
                    {...testIdProp(`sign-in-security-passkey-${passkey.id}`)}
                  >
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {passkeyLabel(passkey)}
                      </span>
                      <span className={QUIET}>{passkeyDetail(passkey, now)}</span>
                    </div>
                    {offers && (
                      <RowActions
                        label={`Actions for ${passkeyLabel(passkey)}`}
                        disabled={removing !== null}
                        actions={[
                          {
                            label: 'Rename',
                            onSelect: () => setRenaming(passkey),
                            testId: `sign-in-security-rename-${passkey.id}`,
                          },
                          {
                            label: 'Remove',
                            onSelect: () => setConfirmingRemoval(passkey),
                            destructive: true,
                            testId: `sign-in-security-remove-${passkey.id}`,
                          },
                        ]}
                        testId={`sign-in-security-actions-${passkey.id}`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {passkeys.length > 0 && renderLost !== undefined && offers && (
              <button
                type="button"
                onClick={() => setResetting({ factor: 'passkey' })}
                className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                {...testIdProp('sign-in-security-cant-use-passkey')}
              >
                Can’t use a passkey?
              </button>
            )}
          </div>
        )}
        <AuthenticatorRows uid={uid} controls={authenticator} locked={locked} />
      </CardBody>
      {renaming !== null && (
        <RenameDialog
          passkey={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={() => {
            setRenaming(null);
            void queryClient.invalidateQueries({ queryKey: MY_PASSKEYS_QUERY_KEY });
          }}
        />
      )}
      {confirmingRemoval !== null && (
        <RemoveDialog
          onCancel={() => setConfirmingRemoval(null)}
          onRemove={() => void onRemove(confirmingRemoval)}
        />
      )}
      {resetting !== null && renderLost !== undefined && (
        <Dialog.Root
          open
          onOpenChange={(next) => {
            if (!next) setResetting(null);
          }}
        >
          <Dialog.Content className="flex flex-col gap-16" testId="sign-in-security-reset-dialog">
            {renderLost({ ...resetting, onClose: () => setResetting(null) })}
          </Dialog.Content>
        </Dialog.Root>
      )}
      {addDialog}
      {removalGate.dialog}
    </Card>
  );
}

/** The card's one line under a sign-in lock (frame E1, DRAFT): why there is nothing to set up. */
function SignInLockLine({ lock }: { lock: SignInLock }) {
  return (
    <div className="px-20 py-16" {...testIdProp('sign-in-security-locked')}>
      <p className="rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {signInLockCardLine(lock)}
      </p>
    </div>
  );
}

/** "Rename": the person's own label for one passkey; an empty name clears it. */
function RenameDialog({
  passkey,
  onClose,
  onRenamed,
}: {
  passkey: PasskeyOut;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(passkey.name ?? '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next && !saving) onClose();
      }}
    >
      <Dialog.Content aria-label="Rename" className="flex flex-col gap-16" testId="passkey-rename">
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Rename
        </Dialog.Title>
        <form
          className="flex flex-col gap-16"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (saving) return;
            setSaving(true);
            setFailed(false);
            void renamePasskey(passkey.id, name).then(onRenamed, () => {
              setFailed(true);
              setSaving(false);
            });
          }}
        >
          <TextField
            label="Name"
            value={name}
            autoFocus
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            testId="passkey-rename-name"
          />
          {failed && <ErrorMessage testId="passkey-rename-failed">{FAILED_LINE}</ErrorMessage>}
          <div className="flex justify-end gap-8">
            <Button
              type="button"
              appearance="saas"
              tone="secondary"
              disabled={saving}
              onClick={onClose}
              testId="passkey-rename-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              appearance="saas"
              tone="primary"
              loading={saving}
              testId="passkey-rename-save"
            >
              Save
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** "Remove this passkey?" — iOS's shipped words, asked before any removal (ruled 2026-10-03). */
function RemoveDialog({ onCancel, onRemove }: { onCancel: () => void; onRemove: () => void }) {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Content
        aria-label="Remove this passkey?"
        className="flex flex-col gap-16"
        testId="passkey-remove"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Remove this passkey?
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          You won’t be able to sign in with it again. Your other ways to sign in are unchanged.
        </Dialog.Description>
        <div className={cn('flex justify-end gap-8')}>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onCancel}
            testId="passkey-remove-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            onClick={onRemove}
            testId="passkey-remove-confirm"
          >
            Remove
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
